/**
 * View Performance Utilities
 *
 * Helper functions for calculating view performance metrics and complexity.
 */

import type { JoinConfig, ViewDependencyInfo } from '@/types/views';

/**
 * Calculate the maximum chain depth for a view based on its dependencies
 *
 * Chain depth represents how many levels of views this view depends on.
 * - Depth 0: Only uses datasets (no view JOINs)
 * - Depth 1: JOINs to views that only use datasets
 * - Depth 2+: JOINs to views that themselves JOIN to other views
 *
 * @param joins - JOIN configurations for the view
 * @param dependencies - Dependency information from the API
 * @returns Maximum chain depth
 */
export function calculateChainDepth(
  joins: JoinConfig[],
  dependencies?: ViewDependencyInfo[]
): number {
  // If no view JOINs, depth is 0
  const hasViewJoins = joins.some((join) => join.source_type === 'view' && join.target_view_id);
  if (!hasViewJoins) {
    return 0;
  }

  // If we have dependency info from the API, we can calculate more accurately
  // For now, we'll use a simple heuristic: count the number of view JOINs
  // A more sophisticated implementation would recursively fetch dependencies

  // Simple heuristic: each view JOIN adds 1 to depth
  // This is conservative and will over-estimate for parallel JOINs
  const viewJoinCount = joins.filter((join) => join.source_type === 'view').length;

  // If we have actual dependency data, use it to get a better estimate
  if (dependencies && dependencies.length > 0) {
    // In a real implementation, we would recursively calculate
    // For now, we assume each dependency adds 1 level
    return Math.max(1, viewJoinCount);
  }

  // Fallback: simple count (minimum depth is 1 if any view JOINs exist)
  return Math.max(1, viewJoinCount);
}

/**
 * Calculate estimated complexity score for a view configuration
 *
 * @param chainDepth - Dependency chain depth
 * @param joinCount - Total number of JOINs
 * @param hasViewJoins - Whether the view has any view-to-view JOINs
 * @returns Complexity score (0-100)
 */
export function calculateComplexityScore(
  chainDepth: number,
  joinCount: number,
  hasViewJoins: boolean
): number {
  let score = 0;

  // Chain depth contributes up to 50 points
  score += Math.min(chainDepth * 10, 50);

  // JOIN count contributes up to 30 points
  score += Math.min(joinCount * 6, 30);

  // View JOINs add 20 points if present
  if (hasViewJoins) {
    score += 20;
  }

  return Math.min(score, 100);
}

/**
 * Check if a view has view-to-view JOINs
 *
 * @param joins - JOIN configurations
 * @returns true if any JOINs target views (not datasets)
 */
export function hasViewJoins(joins: JoinConfig[]): boolean {
  return joins.some((join) => join.source_type === 'view' && join.target_view_id);
}

/**
 * Get performance recommendation based on complexity
 *
 * @param chainDepth - Dependency chain depth
 * @param joinCount - Total number of JOINs
 * @param hasViewJoins - Whether the view has view-to-view JOINs
 * @returns Recommendation text
 */
export function getPerformanceRecommendation(
  chainDepth: number,
  joinCount: number,
  hasViewJoins: boolean
): string {
  if (chainDepth > 5) {
    return 'Materialize this view to improve query performance';
  }
  if (chainDepth >= 4 || (hasViewJoins && joinCount > 4)) {
    return 'Consider materializing for frequently accessed data';
  }
  if (chainDepth >= 2 || (hasViewJoins && joinCount >= 2)) {
    return 'Monitor performance and materialize if queries are slow';
  }
  return 'Performance should be acceptable for most queries';
}
